import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";

import { getEstaciones } from "../helpers/rutaEstaciones";
import { getViajes } from "../helpers/rutaViajes";
import { getPagoEstaciones, postPagoEstacion } from "../helpers/rutaPagoEstacion";
import { getSaldoInicialEstaciones } from "../helpers/rutaSaldoInicialEstacion";
import { getCajas } from "../helpers/rutaCajas";
import { getInformeEstaciones } from "../helpers/rutaReportes";

const num = (v) => Number(v || 0) || 0;

const todayLocalISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const firstDayOfMonthISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  local.setDate(1);
  return local.toISOString().slice(0, 10);
};

const nowLocalDateTimeInput = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const money = (n) =>
  num(n).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getId = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v?._id === "string") return v._id;
  if (typeof v?.id === "string") return v.id;
  if (v?._id?.$oid) return v._id.$oid;
  return "";
};

const getDateValue = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (v?.$date) return v.$date;
  return "";
};

function dateOnly(value) {
  const raw = getDateValue(value) || value;
  if (!raw) return "";

  const s = String(raw).trim();

  // Si ya viene solo fecha, la respetamos tal cual
  const pure = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (pure) return pure[0];

  // Para datetime ISO / Z / offset, convertir a fecha LOCAL
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, "0"),
      String(d.getDate()).padStart(2, "0"),
    ].join("-");
  }

  // fallback por si viene algo raro pero empieza como fecha
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;

  return "";
}

const norm = (v) =>
  String(v || "")
    .trim()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase();

const esMetodoCuentaCorriente = (metodo) => {
  const m = norm(metodo);
  return ["CTA. CTE.", "CTA CTE", "CTA.CTE.", "CUENTA CORRIENTE"].includes(m);
};

const normalizarEstaciones = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.estaciones)) return resp.estaciones;
  if (Array.isArray(resp.data?.estaciones)) return resp.data.estaciones;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const normalizarViajes = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.viajes)) return resp.viajes;
  if (Array.isArray(resp.data?.viajes)) return resp.data.viajes;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const normalizarPagos = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.pagos)) return resp.pagos;
  if (Array.isArray(resp.data?.pagos)) return resp.data.pagos;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const normalizarSaldos = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.saldos)) return resp.saldos;
  if (Array.isArray(resp.data?.saldos)) return resp.data.saldos;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const normalizarCajas = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.cajas)) return resp.cajas;
  if (Array.isArray(resp.data?.cajas)) return resp.data.cajas;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const getEstacionLabel = (e) => {
  const txt = [e?.estacion, e?.lugar].filter(Boolean).join(" - ").trim().toUpperCase();
  return e?.codest ? `${txt} - ${e.codest}` : txt;
};

const getEstacionNombreBase = (e) =>
  [e?.estacion, e?.lugar]
    .filter(Boolean)
    .join(" - ")
    .trim()
    .toUpperCase();

const getSaldoInformeEstacion = (informe, estacionSeleccionada) => {
  const estacionesInforme = Array.isArray(informe?.estaciones)
    ? informe.estaciones
    : [];

  const idSeleccionado = getId(estacionSeleccionada);
  const nombreSeleccionado = norm(getEstacionNombreBase(estacionSeleccionada));

  const fila = estacionesInforme.find((x) => {
    const idInforme =
      getId(x?.estacionId) ||
      getId(x?.estacion?._id) ||
      getId(x?._id) ||
      "";

    const nombreInforme = norm(x?.estacion || x?.nombre || "");

    if (idSeleccionado && idInforme && idSeleccionado === idInforme) {
      return true;
    }

    return nombreInforme === nombreSeleccionado;
  });

  if (!fila) return null;

  const debe = num(fila?.debe);
  const saldo = num(fila?.saldo);

  if (debe > 0) return Number(debe.toFixed(2));
  if (saldo > 0) return Number(saldo.toFixed(2));

  return 0;
};

const errorToText = (err) => {
  if (!err) return "Ocurrió un error";
  if (typeof err === "string") return err;
  if (typeof err?.error === "string") return err.error;
  if (typeof err?.msg === "string") return err.msg;
  if (typeof err?.message === "string") return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return "Ocurrió un error";
  }
};

const getMontoAplicacion = (a) =>
  num(
    a?.montoaplicado ??
      a?.montoAplicado ??
      a?.monto_aplicado ??
      a?.aplicado ??
      a?.monto
  );

const getMontoPago = (p) =>
  num(
    p?.montototal ??
      p?.montoTotal ??
      p?.monto ??
      p?.pago ??
      p?.importe
  );

const getTipoAplicacion = (a) =>
  norm(a?.origenTipo || a?.tipoOrigen || a?.tipomov || a?.tipo || "");

const getCombustibleIdAplicacion = (a) =>
  getId(a?.combustibleId) ||
  getId(a?.combustible) ||
  getId(a?.idCombustible) ||
  getId(a?.origenId) ||
  getId(a?.idorigen) ||
  getId(a?.idOrigen) ||
  "";

const getSaldoInicialIdAplicacion = (a) =>
  getId(a?.saldoInicialId) ||
  getId(a?.saldoInicial) ||
  getId(a?.idSaldoInicial) ||
  getId(a?.origenId) ||
  getId(a?.idorigen) ||
  getId(a?.idOrigen) ||
  "";

export default function ModalPagoEstacion({ show, setShow, onGuardado }) {
  const mountedRef = useRef(false);

  const [estaciones, setEstaciones] = useState([]);
  const [viajes, setViajes] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [saldoRealInforme, setSaldoRealInforme] = useState(null);

  const [cargandoBase, setCargandoBase] = useState(false);
  const [cargandoMovs, setCargandoMovs] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const [formValues, setFormValues] = useState({
    estacion: "",
    desde: firstDayOfMonthISO(),
    hasta: todayLocalISO(),
    fechapago: nowLocalDateTimeInput(),
    mediopago: "EFECTIVO",
    montopagado: "",
    observaciones: "",
    caja: "",
  });

  const [seleccion, setSeleccion] = useState({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resetForm = () => {
    setViajes([]);
    setPagos([]);
    setSaldos([]);
    setSeleccion({});
    setSaldoRealInforme(null);
    setError("");
    setFormValues({
      estacion: "",
      desde: firstDayOfMonthISO(),
      hasta: todayLocalISO(),
      fechapago: nowLocalDateTimeInput(),
      mediopago: "EFECTIVO",
      montopagado: "",
      observaciones: "",
      caja: "",
    });
  };

  const handleClose = () => {
    resetForm();
    setShow(false);
  };

  useEffect(() => {
    if (!show) return;

    let activo = true;

    const cargarInicial = async () => {
      if (activo && mountedRef.current) {
        setCargandoBase(true);
        setError("");
      }

      try {
        const [respEstaciones, respCajas] = await Promise.all([
          getEstaciones(),
          getCajas(),
        ]);

        if (activo && mountedRef.current) {
          setEstaciones(
            normalizarEstaciones(respEstaciones)
              .filter((e) => e?.activo !== false)
              .sort((a, b) =>
                getEstacionLabel(a).localeCompare(getEstacionLabel(b), "es", {
                  sensitivity: "base",
                })
              )
          );

          setCajas(
            normalizarCajas(respCajas)
              .filter((c) => c?.activo !== false)
              .sort((a, b) =>
                String(a?.descripcion || "").localeCompare(
                  String(b?.descripcion || ""),
                  "es",
                  { sensitivity: "base" }
                )
              )
          );
        }
      } catch (e) {
        if (activo && mountedRef.current) {
          setError("No se pudieron cargar estaciones/cajas");
        }
      } finally {
        if (activo && mountedRef.current) {
          setCargandoBase(false);
        }
      }
    };

    cargarInicial();

    return () => {
      activo = false;
    };
  }, [show]);

  const handleChange = ({ target }) => {
    const { name, value } = target;

    setFormValues((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "mediopago" && value !== "EFECTIVO") {
        next.caja = "";
      }

      return next;
    });
  };

  const cajaSeleccionada = useMemo(() => {
    return cajas.find((c) => String(c._id) === String(formValues.caja)) || null;
  }, [cajas, formValues.caja]);

  const cargarMovimientos = async () => {
    if (!formValues.estacion) {
      setError("Seleccioná una estación");
      return;
    }

    setCargandoMovs(true);
    setError("");
    setSeleccion({});
    setViajes([]);
    setPagos([]);
    setSaldos([]);
    setSaldoRealInforme(null);

    try {
      const estacionSeleccionada =
        estaciones.find((e) => getId(e) === formValues.estacion) || null;

      const fechaCorte = formValues.hasta || todayLocalISO();

      const [respViajes, respPagos, respSaldos, respInforme] =
        await Promise.all([
          getViajes(),
          getPagoEstaciones({ estacion: formValues.estacion }),
          getSaldoInicialEstaciones({ estacion: formValues.estacion }),
          getInformeEstaciones(fechaCorte),
        ]);

      setViajes(normalizarViajes(respViajes));
      setPagos(normalizarPagos(respPagos).filter((p) => p?.estado === "activo"));
      setSaldos(
        normalizarSaldos(respSaldos).filter((s) => s?.estado === "activo")
      );

      const saldoInforme = getSaldoInformeEstacion(
        respInforme,
        estacionSeleccionada
      );

      setSaldoRealInforme(saldoInforme);
    } catch (e) {
      setError("No se pudieron cargar los movimientos");
    } finally {
      setCargandoMovs(false);
    }
  };

  const pagosHastaCorte = useMemo(() => {
    const hasta = formValues.hasta || "";

    return (pagos || []).filter((p) => {
      if (p?.estado && p.estado !== "activo") return false;

      const fechaPago = dateOnly(
        p?.fechapago || p?.fechaPago || p?.fecha || p?.createdAt
      );

      if (hasta && fechaPago && fechaPago > hasta) return false;

      return true;
    });
  }, [pagos, formValues.hasta]);

  const aplicadoCombustibleMap = useMemo(() => {
    const map = {};

    pagosHastaCorte.forEach((p) => {
      (p?.aplicaciones || []).forEach((a) => {
        const tipo = getTipoAplicacion(a);

        const tieneCampoCombustible =
          a?.combustibleId || a?.combustible || a?.idCombustible;

        if (tipo !== "COMBUSTIBLE" && !tieneCampoCombustible) return;

        const id = getCombustibleIdAplicacion(a);
        if (!id) return;

        map[id] = num(map[id]) + getMontoAplicacion(a);
      });
    });

    return map;
  }, [pagosHastaCorte]);

  const aplicadoSaldoMap = useMemo(() => {
    const map = {};

    pagosHastaCorte.forEach((p) => {
      (p?.aplicaciones || []).forEach((a) => {
        const tipo = getTipoAplicacion(a);

        const tieneCampoSaldo =
          a?.saldoInicialId || a?.saldoInicial || a?.idSaldoInicial;

        if (tipo !== "SALDO_INICIAL" && !tieneCampoSaldo) return;

        const id = getSaldoInicialIdAplicacion(a);
        if (!id) return;

        map[id] = num(map[id]) + getMontoAplicacion(a);
      });
    });

    return map;
  }, [pagosHastaCorte]);

  const totalPagosHastaCorte = useMemo(() => {
    return Number(
      pagosHastaCorte
        .reduce((acc, p) => acc + getMontoPago(p), 0)
        .toFixed(2)
    );
  }, [pagosHastaCorte]);

  const totalAplicadoDetectadoEnFilas = useMemo(() => {
    const totalCombustible = Object.values(aplicadoCombustibleMap).reduce(
      (acc, v) => acc + num(v),
      0
    );

    const totalSaldo = Object.values(aplicadoSaldoMap).reduce(
      (acc, v) => acc + num(v),
      0
    );

    return Number((totalCombustible + totalSaldo).toFixed(2));
  }, [aplicadoCombustibleMap, aplicadoSaldoMap]);

  const creditoLibreParaModal = useMemo(() => {
    return Number(
      Math.max(0, totalPagosHastaCorte - totalAplicadoDetectadoEnFilas).toFixed(2)
    );
  }, [totalPagosHastaCorte, totalAplicadoDetectadoEnFilas]);

  const pendientesBase = useMemo(() => {
    const rows = [];
    const desde = formValues.desde || "";
    const hasta = formValues.hasta || "";

    saldos.forEach((s) => {
      const sid = getId(s);
      const fecha = dateOnly(s?.fecha);
      const monto = num(s?.monto);
      const aplicado = num(aplicadoSaldoMap[sid]);
      const saldo = Number((monto - aplicado).toFixed(2));

      const fechaOk = (!desde || fecha >= desde) && (!hasta || fecha <= hasta);

      if (!fechaOk || saldo <= 0) return;

      rows.push({
        key: `SALDO_INICIAL-${sid}`,
        origenTipo: "SALDO_INICIAL",
        saldoInicialId: sid,
        viaje: null,
        nroviaje: 0,
        combustibleId: null,
        fecha,
        localidad: "",
        tipo: "SALDO INICIAL",
        montooriginal: monto,
        aplicadoprevio: aplicado,
        saldopendiente: saldo,
      });
    });

    viajes
      .filter((v) => (v?.estado ? v.estado === "activo" : true))
      .forEach((v) => {
        const viajeId = getId(v);
        const nroviaje = Number(v?.nroViaje || v?.nroviaje || 0);

        (Array.isArray(v?.combustible) ? v.combustible : []).forEach((c) => {
          const estacionId = getId(c?.estacion);
          const combustibleId = getId(c);
          const monto = num(c?.monto);
          const fecha = dateOnly(c?.fecha || v?.fechaInicio || v?.createdAt);
          const metodo = c?.metodoPago || "";

          const fechaOk = (!desde || fecha >= desde) && (!hasta || fecha <= hasta);

          if (!fechaOk) return;
          if (!estacionId || estacionId !== formValues.estacion) return;
          if (!combustibleId) return;
          if (monto <= 0) return;
          if (!esMetodoCuentaCorriente(metodo)) return;

          const aplicado = num(aplicadoCombustibleMap[combustibleId]);
          const saldo = Number((monto - aplicado).toFixed(2));

          if (saldo <= 0) return;

          rows.push({
            key: `COMBUSTIBLE-${combustibleId}`,
            origenTipo: "COMBUSTIBLE",
            saldoInicialId: null,
            viaje: viajeId,
            nroviaje,
            combustibleId,
            fecha,
            localidad: c?.localidad || "",
            tipo: "COMBUSTIBLE",
            montooriginal: monto,
            aplicadoprevio: aplicado,
            saldopendiente: saldo,
          });
        });
      });

    return rows.sort((a, b) => {
      if (a.fecha < b.fecha) return -1;
      if (a.fecha > b.fecha) return 1;
      return a.nroviaje - b.nroviaje;
    });
  }, [
    viajes,
    saldos,
    aplicadoCombustibleMap,
    aplicadoSaldoMap,
    formValues.estacion,
    formValues.desde,
    formValues.hasta,
  ]);

  const saldoRealParaPagar = useMemo(() => {
    if (saldoRealInforme === null || saldoRealInforme === undefined) return null;

    return Number(Math.max(0, num(saldoRealInforme)).toFixed(2));
  }, [saldoRealInforme]);

  const pendientes = useMemo(() => {
    let credito = Number(creditoLibreParaModal.toFixed(2));

    const rowsConCredito =
      credito <= 0
        ? pendientesBase
        : pendientesBase
            .map((item) => {
              const saldoActual = num(item.saldopendiente);

              if (credito <= 0) return item;

              const descuento = Math.min(saldoActual, credito);
              credito = Number((credito - descuento).toFixed(2));

              const nuevoSaldo = Number((saldoActual - descuento).toFixed(2));

              return {
                ...item,
                aplicadoprevio: Number(
                  (num(item.aplicadoprevio) + descuento).toFixed(2)
                ),
                saldopendiente: nuevoSaldo,
              };
            })
            .filter((item) => num(item.saldopendiente) > 0);

    if (saldoRealParaPagar === null) {
      return rowsConCredito;
    }

    let restante = Number(saldoRealParaPagar.toFixed(2));

    if (restante <= 0) return [];

    const rowsCapadas = [];

    for (const item of rowsConCredito) {
      if (restante <= 0) break;

      const saldoItem = num(item.saldopendiente);
      const montoMostrar = Math.min(saldoItem, restante);

      rowsCapadas.push({
        ...item,
        saldopendiente: Number(montoMostrar.toFixed(2)),
        montooriginal: item.montooriginal,
      });

      restante = Number((restante - montoMostrar).toFixed(2));
    }

    return rowsCapadas;
  }, [pendientesBase, creditoLibreParaModal, saldoRealParaPagar]);
  
  const handleToggle = (item) => {
    const key = item.key;

    setSeleccion((prev) => {
      const copia = { ...prev };

      if (copia[key]) {
        delete copia[key];
        return copia;
      }

      copia[key] = {
        ...item,
        montoaplicado: Number(item.saldopendiente.toFixed(2)),
      };

      return copia;
    });
  };

  const handleTildarTodos = () => {
    setSeleccion((prev) => {
      const copia = { ...prev };

      pendientes.forEach((item) => {
        if (item.saldopendiente <= 0) return;

        copia[item.key] = {
          ...item,
          montoaplicado: num(copia[item.key]?.montoaplicado)
            ? num(copia[item.key]?.montoaplicado)
            : Number(item.saldopendiente.toFixed(2)),
        };
      });

      return copia;
    });
  };

  const handleDestildarTodos = () => {
    setSeleccion({});
  };

  const localDateTimeToOffsetISO = (value) => {
    if (!value) return null;

    const d = new Date(value.length === 16 ? `${value}:00` : value);
    const offsetMin = -d.getTimezoneOffset();
    const sign = offsetMin >= 0 ? "+" : "-";
    const hh = String(Math.floor(Math.abs(offsetMin) / 60)).padStart(2, "0");
    const mm = String(Math.abs(offsetMin) % 60).padStart(2, "0");

    const [datePart, timePartRaw = "00:00"] = value.split("T");
    const timePart = timePartRaw.slice(0, 5);

    return `${datePart}T${timePart}:00${sign}${hh}:${mm}`;
  };

  const handleMontoAplicado = (item, value) => {
    const limpio = String(value).replace(",", ".");
    let monto = Number(limpio);

    if (Number.isNaN(monto) || monto < 0) monto = 0;
    if (monto > item.saldopendiente) monto = item.saldopendiente;

    setSeleccion((prev) => ({
      ...prev,
      [item.key]: {
        ...item,
        montoaplicado: Number(monto.toFixed(2)),
      },
    }));
  };

  const aplicacionesSeleccionadas = useMemo(() => {
    return Object.values(seleccion).filter((x) => num(x?.montoaplicado) > 0);
  }, [seleccion]);

  const totalAplicado = useMemo(() => {
    return Number(
      aplicacionesSeleccionadas
        .reduce((acc, item) => acc + num(item.montoaplicado), 0)
        .toFixed(2)
    );
  }, [aplicacionesSeleccionadas]);

  const montoPagado = useMemo(() => {
    const raw = String(formValues.montopagado ?? "").trim();

    if (raw === "") {
      return Number(totalAplicado.toFixed(2));
    }

    const limpio = raw.replace(",", ".");
    const monto = Number(limpio);

    if (Number.isNaN(monto) || monto < 0) return 0;

    return Number(monto.toFixed(2));
  }, [formValues.montopagado, totalAplicado]);

  const saldoAFavorGenerado = useMemo(() => {
    return Number(Math.max(0, montoPagado - totalAplicado).toFixed(2));
  }, [montoPagado, totalAplicado]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formValues.estacion) {
      setError("Seleccioná una estación");
      return;
    }

    if (montoPagado <= 0 && totalAplicado <= 0) {
      setError("Seleccioná movimientos o cargá un monto pagado");
      return;
    }

    if (montoPagado <= 0) {
      setError("El monto pagado debe ser mayor a 0");
      return;
    }

    if (montoPagado < totalAplicado) {
      setError("El monto pagado no puede ser menor que el total aplicado");
      return;
    }

    if (formValues.mediopago === "EFECTIVO") {
      if (!formValues.caja) {
        setError("Seleccioná la caja desde donde sale el dinero");
        return;
      }

      if (num(cajaSeleccionada?.saldoActual) < montoPagado) {
        setError(
          `Saldo insuficiente en la caja seleccionada. Disponible: $ ${money(
            cajaSeleccionada?.saldoActual
          )}`
        );
        return;
      }
    }

    const payload = {
      estacion: formValues.estacion,
      fechapago: localDateTimeToOffsetISO(formValues.fechapago),
      montototal: montoPagado,
      mediopago: formValues.mediopago,
      observaciones: formValues.observaciones,
      aplicaciones: aplicacionesSeleccionadas.map((a) => ({
        origenTipo: a.origenTipo,
        viaje: a.viaje,
        nroviaje: a.nroviaje,
        combustibleId: a.combustibleId,
        saldoInicialId: a.saldoInicialId,
        localidad: a.localidad,
        montooriginal: a.montooriginal,
        montoaplicado: a.montoaplicado,
      })),
      caja: formValues.mediopago === "EFECTIVO" ? formValues.caja : null,
    };

    setGuardando(true);
    setError("");

    try {
      const resp = await postPagoEstacion(payload);

      if (resp?.ok === false) {
        setError(errorToText(resp));
        return;
      }

      if (onGuardado) await onGuardado(resp);
      handleClose();
    } catch (e) {
      setError(errorToText(e?.response?.data || e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      <style>
        {`
        .tabla-pago-estacion-wrapper {
          position: relative;
          overflow: auto;
          contain: paint;
          max-height: calc(100vh - 440px);
          min-height: 210px;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          background: #ffffff;
        }

        .tabla-pago-estacion .td-check {
          width: 42px;
          min-width: 42px;
          max-width: 42px;
          text-align: center !important;
          vertical-align: middle !important;
          padding: 0 !important;
          overflow: hidden;
        }

        .tabla-pago-estacion .check-pago-estacion {
          float: none !important;
          position: static !important;
          margin: 0 !important;
          transform: none !important;
          vertical-align: middle;
          cursor: pointer;
        }
          .modal-pago-estacion-dialog {
            width: 96vw !important;
            max-width: 96vw !important;
            margin: 0.75rem auto !important;
          }

          .modal-pago-estacion-dialog .modal-content {
            max-height: 96vh;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .modal-pago-estacion-form {
            min-height: 0;
            max-height: 96vh;
            display: flex;
            flex-direction: column;
          }

          .modal-pago-estacion-body {
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
            padding: 0.75rem;
          }

          .modal-pago-estacion-footer {
            flex: 0 0 auto;
            background: #ffffff;
            border-top: 1px solid #dee2e6;
            padding: 0.55rem 0.75rem;
          }

          .modal-pago-estacion-body .form-label {
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 0.15rem;
          }

          .modal-pago-estacion-body .form-control,
          .modal-pago-estacion-body .form-select {
            font-size: 12px;
            min-height: 32px;
            padding: 0.25rem 0.45rem;
          }

          .tabla-pago-estacion-wrapper {
            max-height: calc(100vh - 440px);
            min-height: 210px;
            overflow: auto;
            border: 1px solid #dee2e6;
            border-radius: 6px;
            background: #ffffff;
          }

          .tabla-pago-estacion {
            min-width: 1050px;
            margin-bottom: 0;
            font-size: 11px;
          }

          .tabla-pago-estacion thead th {
            position: sticky;
            top: 0;
            z-index: 5;
            background: #f8f9fa;
            text-align: center;
            vertical-align: middle;
            white-space: nowrap;
            font-size: 11px;
            padding: 0.35rem;
          }

          .tabla-pago-estacion tbody td {
            vertical-align: middle;
            white-space: nowrap;
            padding: 0.25rem 0.35rem;
          }

          .tabla-pago-estacion .form-control {
            height: 28px;
            font-size: 11px;
            padding: 0.15rem 0.35rem;
          }

          .resumen-pago-estacion {
            font-size: 12px;
            padding: 0.55rem !important;
            margin-top: 0.55rem !important;
          }

          .saldo-caja-box {
            padding: 0.6rem !important;
          }

          .saldo-caja-box strong {
            font-size: 12px;
          }

          @media (max-height: 760px) {
            .tabla-pago-estacion-wrapper {
              max-height: calc(100vh - 390px);
              min-height: 170px;
            }

            .modal-pago-estacion-body {
              padding: 0.55rem;
            }

            .modal-pago-estacion-body .mb-3 {
              margin-bottom: 0.45rem !important;
            }
          }
        `}
      </style>

      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        backdrop="static"
        dialogClassName="modal-pago-estacion-dialog"
      >
        <Form onSubmit={handleSubmit} className="modal-pago-estacion-form">
          <Modal.Header closeButton>
            <Modal.Title>Pago a estación</Modal.Title>
          </Modal.Header>

          <Modal.Body className="modal-pago-estacion-body">
            {error && <Alert variant="danger">{error}</Alert>}

            <Row className="mb-2 g-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Estación</Form.Label>
                  <Form.Select
                    name="estacion"
                    value={formValues.estacion}
                    onChange={handleChange}
                    disabled={cargandoBase || guardando}
                  >
                    <option value="">Seleccione estación</option>
                    {estaciones.map((e) => (
                      <option key={e._id} value={e._id}>
                        {getEstacionLabel(e)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Desde</Form.Label>
                  <Form.Control
                    type="date"
                    name="desde"
                    value={formValues.desde}
                    onChange={handleChange}
                    disabled={guardando}
                  />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    name="hasta"
                    value={formValues.hasta}
                    onChange={handleChange}
                    disabled={guardando}
                  />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Fecha pago</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="fechapago"
                    value={formValues.fechapago}
                    onChange={handleChange}
                    disabled={guardando}
                  />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>&nbsp;</Form.Label>
                  <div className="d-grid">
                    <Button
                      variant="primary"
                      type="button"
                      onClick={cargarMovimientos}
                      disabled={cargandoMovs || guardando || !formValues.estacion}
                      size="sm"
                    >
                      {cargandoMovs ? (
                        <>
                          <Spinner size="sm" animation="border" className="me-2" />
                          Cargando
                        </>
                      ) : (
                        "Buscar"
                      )}
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-2 g-2">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Medio de pago</Form.Label>
                  <Form.Select
                    name="mediopago"
                    value={formValues.mediopago}
                    onChange={handleChange}
                    disabled={guardando}
                  >
                    <option value="EFECTIVO">EFECTIVO</option>
                    <option value="TRANSFERENCIA">TRANSFERENCIA</option>
                    <option value="CHEQUE">CHEQUE</option>
                    <option value="OTRO">OTRO</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Monto pagado / transferido</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    name="montopagado"
                    value={formValues.montopagado}
                    onChange={handleChange}
                    disabled={guardando}
                    placeholder={String(totalAplicado || "")}
                  />
                  <Form.Text muted style={{ fontSize: 10 }}>
                    Si lo dejás vacío, toma el total aplicado.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    type="text"
                    name="observaciones"
                    value={formValues.observaciones}
                    onChange={handleChange}
                    disabled={guardando}
                  />
                </Form.Group>
              </Col>
            </Row>

            {formValues.mediopago === "EFECTIVO" && (
              <Row className="mb-2 g-2">
                <Col md={7}>
                  <Form.Group>
                    <Form.Label>Caja</Form.Label>
                    <Form.Select
                      name="caja"
                      value={formValues.caja}
                      onChange={handleChange}
                      disabled={guardando}
                    >
                      <option value="">Seleccione caja</option>
                      {cajas.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.descripcion} - Saldo $ {money(c.saldoActual)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={5}>
                  <div className="border rounded bg-light h-100 saldo-caja-box">
                    <strong>Saldo disponible</strong>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color:
                          num(cajaSeleccionada?.saldoActual) >= montoPagado
                            ? "#198754"
                            : "#dc3545",
                      }}
                    >
                      $ {money(cajaSeleccionada?.saldoActual)}
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {pendientes.length > 0 ? (
              <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex gap-2">
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    onClick={handleTildarTodos}
                    disabled={guardando || pendientes.length === 0}
                  >
                    Tildar todos
                  </Button>

                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleDestildarTodos}
                    disabled={guardando || aplicacionesSeleccionadas.length === 0}
                  >
                    Destildar todos
                  </Button>
                </div>

                <div style={{ fontSize: 12, fontWeight: 600, textAlign: "right" }}>
                  {saldoRealParaPagar !== null && (
                    <div style={{ color: "#198754" }}>
                      Saldo real a pagar: $ {money(saldoRealParaPagar)}
                    </div>
                  )}

                  <div>
                    Pendientes: {pendientes.length} | Seleccionados:{" "}
                    {aplicacionesSeleccionadas.length}
                  </div>
                </div>
              </div>

              <div className="tabla-pago-estacion-wrapper">
                <Table
                  striped
                  bordered
                  hover
                  size="sm"
                  className="tabla-pago-estacion"
                >
                  <thead>
                    <tr>
                      <th style={{ width: 42 }}>Sel</th>
                      <th style={{ width: 95 }}>Fecha</th>
                      <th style={{ width: 130 }}>Tipo</th>
                      <th style={{ width: 65 }}>Viaje</th>
                      <th style={{ width: 170 }}>Localidad</th>
                      <th style={{ width: 135 }}>Monto original</th>
                      <th style={{ width: 115 }}>Aplicado</th>
                      <th style={{ width: 125 }}>Saldo</th>
                      <th style={{ width: 160 }}>Aplicar ahora</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pendientes.map((item) => {
                      const checked = !!seleccion[item.key];
                      const montoAplicado = checked
                        ? num(seleccion[item.key]?.montoaplicado)
                        : 0;

                      return (
                        <tr key={item.key}>
                          <td className="td-check">
                            <input
                              type="checkbox"
                              className="form-check-input check-pago-estacion"
                              checked={checked}
                              onChange={() => handleToggle(item)}
                              disabled={guardando || item.saldopendiente <= 0}
                            />
                          </td>

                          <td>{item.fecha}</td>
                          <td>{item.tipo}</td>
                          <td className="text-center">{item.nroviaje || ""}</td>
                          <td>{item.localidad || ""}</td>

                          <td style={{ textAlign: "right" }}>
                            $ {money(item.montooriginal)}
                          </td>

                          <td style={{ textAlign: "right" }}>
                            $ {money(item.aplicadoprevio)}
                          </td>

                          <td
                            style={{
                              textAlign: "right",
                              fontWeight: 700,
                              color:
                                item.saldopendiente > 0 ? "#198754" : "#dc3545",
                            }}
                          >
                            $ {money(item.saldopendiente)}
                          </td>

                          <td>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={checked ? montoAplicado : ""}
                              onChange={(e) =>
                                handleMontoAplicado(item, e.target.value)
                              }
                              disabled={!checked || guardando}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              </>
            ) : (
              !cargandoMovs && (
                <Alert variant="secondary" className="mb-0">
                  No hay pendientes en el período. Igual podés registrar un pago a
                  cuenta cargando un monto pagado.
                </Alert>
              )
            )}

            {(formValues.estacion ||
              aplicacionesSeleccionadas.length > 0 ||
              montoPagado > 0) && (
              <div className="border rounded bg-light resumen-pago-estacion">
                <Row className="g-2">
                  <Col md={3}>
                    <strong>Movimientos seleccionados:</strong>{" "}
                    {aplicacionesSeleccionadas.length}
                  </Col>

                  <Col md={3}>
                    <strong>Total aplicado:</strong> $ {money(totalAplicado)}
                  </Col>

                  <Col md={3}>
                    <strong>Monto pagado:</strong> $ {money(montoPagado)}
                  </Col>

                  <Col md={3}>
                    <strong>Saldo a favor generado:</strong>{" "}
                    <span
                      style={{
                        color: saldoAFavorGenerado > 0 ? "#0d6efd" : "#198754",
                        fontWeight: 700,
                      }}
                    >
                      $ {money(saldoAFavorGenerado)}
                    </span>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer className="modal-pago-estacion-footer">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={guardando}
              size="sm"
            >
              Cancelar
            </Button>

            <Button
              variant="success"
              type="submit"
              disabled={guardando}
              size="sm"
            >
              {guardando ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Guardando
                </>
              ) : (
                "Guardar pago"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}